import { echapper } from './echapper.js';

/**
 * LA MARQUE DE L'ORG SUR UN DOCUMENT : en-tete et pied, en HTML a styles en
 * ligne, sans aucune ressource exterieure.
 *
 * Un module, deux usages : le PDF (ce package) et le canevas des mails du socle,
 * qui le RECOPIE dans `functions/` (`functions/` ne depend d'aucun `@taki/*`,
 * `docs/FUNCTIONS.md`) avec un test d'egalite, comme `entreeDAnnuaire`. D'ou les
 * styles EN LIGNE et les tableaux plutot qu'une feuille de style : un client
 * mail ignore les `<style>`, Chromium les lit, les deux lisent un `style=`.
 *
 * Ce module ne lit rien : il recoit la marque deja resolue (le document
 * `orgs/{orgId}/settings/branding`, sinon le `branding` du `taki.config.json`,
 * `packages/sdk/src/branding.ts`). Une valeur douteuse (couleur qui n'est pas un
 * hex, logo qui n'est pas une image en data URL) est ignoree, jamais recopiee :
 * le nom de l'org s'affiche a la place du logo, l'encre neutre a la place de
 * l'accent.
 */

/** Ce qu'un document sait de la marque de l'org. */
export interface MarqueDeDocument {
  /** Le nom de l'environnement, affiche en toutes lettres. */
  nom: string;
  /** Le logo, en data URL (png, jpeg, webp ou svg) ; absent : le nom seul. */
  logo?: string | null;
  /** La couleur d'accent, `#rgb` ou `#rrggbb` ; absente : l'encre neutre. */
  accent?: string | null;
}

/**
 * La palette du document, hors accent. Un PDF ne lit pas les tokens CSS de
 * l'ecran (`@taki/ui`) : il doit se suffire a lui-meme, et un gris d'imprimerie
 * n'est pas une couleur de marque.
 */
export const ENCRE = { texte: '#1f2328', doux: '#59636e', filet: '#d1d9e0', fond: '#f6f8fa', neutre: '#1f2328' } as const;

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const LOGO = /^data:image\/(?:png|jpeg|webp|svg\+xml)[;,]/i;

/** L'accent de la marque s'il est sain, sinon l'encre neutre. */
export function accentDe(marque: MarqueDeDocument): string {
  return marque.accent && HEX.test(marque.accent.trim()) ? marque.accent.trim().toLowerCase() : ENCRE.neutre;
}

function base64Utf8(texte: string): string {
  let binaire = '';
  for (const octet of new TextEncoder().encode(texte)) binaire += String.fromCharCode(octet);
  return btoa(binaire);
}

/**
 * Le logo s'il est une image en data URL, sinon `null`. Un SVG ecrit en clair
 * (`data:image/svg+xml,<svg ...>`, que le branding accepte) est re-encode en
 * base64 : en clair, ses guillemets casseraient l'attribut et ses `url(#...)`
 * internes feraient refuser le document par le serveur.
 */
export function logoDe(marque: MarqueDeDocument): string | null {
  const logo = marque.logo?.trim();
  if (!logo || !LOGO.test(logo)) return null;
  const virgule = logo.indexOf(',');
  if (/;base64$/i.test(logo.slice(0, virgule))) return /^[A-Za-z0-9+/=]*$/.test(logo.slice(virgule + 1)) ? logo : null;
  if (!/^data:image\/svg\+xml/i.test(logo)) return null;
  const brut = logo.slice(virgule + 1);
  let texte = brut;
  try { texte = decodeURIComponent(brut); } catch { /* deja en clair */ }
  return `data:image/svg+xml;base64,${base64Utf8(texte)}`;
}

/**
 * Le texte lisible SUR l'accent : blanc sur une couleur foncee, encre sur une
 * claire (luminance relative WCAG, seuil a mi-chemin). Une marque lime ou jaune
 * ne doit pas porter un titre blanc illisible.
 */
export function texteSurAccent(accent: string): string {
  const hex = accent.length === 4 ? accent.slice(1).split('').map((c) => c + c).join('') : accent.slice(1);
  const [r, g, b] = [0, 2, 4].map((i) => {
    const canal = parseInt(hex.slice(i, i + 2), 16) / 255;
    return canal <= 0.03928 ? canal / 12.92 : ((canal + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.179 ? ENCRE.texte : '#ffffff';
}

export interface OptionsDEntete {
  /** Le titre du document (« Fiche matériel », « Devis D-2026-014 »). */
  titre: string;
  /** Une ligne sous le titre (date, reference) ; texte, echappe ici. */
  sousTitre?: string;
}

/**
 * L'EN-TETE : le logo (ou le nom) a gauche, le titre et le sous-titre dessous,
 * un filet a la couleur d'accent. Un tableau, pour que le mail le rende aussi.
 */
export function enteteOrg(marque: MarqueDeDocument, options: OptionsDEntete): string {
  const accent = accentDe(marque);
  const logo = logoDe(marque);
  const nom = echapper(marque.nom);
  const identite = logo
    ? `<img src="${logo}" alt="${nom}" style="display:block;height:40px;width:auto;max-width:220px;border:0">`
    : `<span style="font-size:18px;font-weight:700;color:${accent}">${nom}</span>`;
  const sousTitre = options.sousTitre
    ? `<div style="margin-top:4px;font-size:12px;color:${ENCRE.doux}">${echapper(options.sousTitre)}</div>`
    : '';
  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border-bottom:3px solid ${accent};margin-bottom:24px">` +
    `<tr><td style="padding:0 0 12px 0;vertical-align:middle">${identite}</td>` +
    `<td style="padding:0 0 12px 0;vertical-align:middle;text-align:right;font-size:12px;color:${ENCRE.doux}">${logo ? nom : ''}</td></tr>` +
    `<tr><td colspan="2" style="padding:0 0 14px 0"><div style="font-size:22px;font-weight:700;color:${ENCRE.texte}">${echapper(options.titre)}</div>${sousTitre}</td></tr>` +
    `</table>`
  );
}

export interface OptionsDePied {
  /** La phrase du pied ; par defaut « Document produit par MyTaki pour <org> ». */
  mention?: string;
}

/** LE PIED : une ligne discrete sous un filet, au nom de l'org. */
export function piedOrg(marque: MarqueDeDocument, options: OptionsDePied = {}): string {
  const mention = options.mention ?? `Document produit par MyTaki pour ${marque.nom}`;
  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border-top:1px solid ${ENCRE.filet};margin-top:32px">` +
    `<tr><td style="padding:10px 0 0 0;font-size:10px;color:${ENCRE.doux}">${echapper(mention)}</td></tr>` +
    `</table>`
  );
}
